import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, Search, X } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import ContactPanel from "./contactsapp-panel";
import { api, apiErrorText } from "@/lib/api";
import { ROLE_LABELS, useAuth, type Role } from "./auth";
import { contactsApi, stagesApi, takeContact, takeImportFilter, type Contact, type ContactQuery, type Folders, type Segment, type Stage } from "./contacts-api";
import { contactEmpty, contactSegments, contactsAppHints, relTime, stageTone, type ContactOwner } from "./contactsapp-meta";
import { goAnchor } from "./app-nav";
import { t } from "@/lib/i18n";

/* Окно «Контакты»: иконная панель приложения, папки по этапам жизненного цикла и сегментам
   (счётчики с сервера), поиск на сервере, честное пустое состояние и боковая панель «Новый контакт».
   Агент видит только свои контакты — это решает сервер. */

const RAIL = [
  { id: "contacts", icon: "ri-contacts-book-2-line", label: t("Контакты"), anchor: "#contacts-app", active: true },
  { id: "inbox", icon: "ri-inbox-line", label: t("Входящие"), anchor: "#inbox-app", active: false },
  { id: "dashboard", icon: "ri-dashboard-line", label: t("Дашборд"), anchor: "#dashboard", active: false },
  { id: "channels", icon: "ri-share-forward-line", label: t("Каналы"), anchor: "#channels", active: false },
  { id: "ai", icon: "ri-sparkling-line", label: "AI Assist", anchor: "#ai-assist", active: false },
  { id: "ai-agents", icon: "ri-robot-2-line", label: t("AI-агенты"), anchor: "#ai-agents", active: false },
  { id: "broadcasts", icon: "ri-mail-send-line", label: t("Рассылки"), anchor: "#broadcasts", active: false },
  { id: "workflows", icon: "ri-flow-chart", label: t("Рабочие процессы"), anchor: "#workflows", active: false },
  { id: "reports", icon: "ri-bar-chart-2-line", label: t("Отчеты"), anchor: "#reports", active: false },
  { id: "lifecycle", icon: "ri-refresh-line", label: t("Жизненный цикл"), anchor: "#lifecycle", active: false },
  { id: "settings", icon: "ri-settings-3-line", label: t("Настройки"), anchor: "#settings", active: false },
  { id: "general", icon: "ri-information-line", label: t("Общие сведения"), anchor: "#general-settings", active: false },
];

const PAGE = 50;

const initials = (name: string) =>
  name
    .replace(/^\+/, "")
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "•";

const isSegment = (id: string): id is Segment => contactSegments.some((s) => s.id === id);

/** иллюстрация пустого состояния: карточка контакта, две «плитки» и строки — без SVG и картинок */
function EmptyArt() {
  return (
    <div className="relative mx-auto h-28 w-44" aria-hidden>
      <div className="absolute end-0 top-1 h-20 w-28 rounded-lg bg-primary/15 p-2">
        <span className="mx-auto block h-1 w-10 rounded-full bg-primary/40" />
        <span className="mx-auto mt-1.5 block size-6 rounded-full bg-primary/40" />
        <span className="mx-auto mt-1 block h-1 w-14 rounded-full bg-primary/25" />
        <span className="mx-auto mt-1 block h-1 w-10 rounded-full bg-primary/20" />
      </div>
      <div className="absolute bottom-2 start-0 flex h-12 w-20 flex-col justify-center gap-1 rounded-lg bg-amber-400/20 px-2">
        <span className="h-1 w-10 rounded-full bg-amber-500/45" />
        <span className="h-1 w-14 rounded-full bg-amber-500/35" />
        <span className="h-1 w-8 rounded-full bg-amber-500/25" />
      </div>
      <div className="absolute start-8 top-6 h-16 w-24 rounded-lg border border-border bg-background p-2">
        <span className="block size-5 rounded-full bg-foreground/15" />
        <span className="mt-1.5 block h-1 w-14 rounded-full bg-foreground/15" />
        <span className="mt-1 block h-1 w-10 rounded-full bg-foreground/10" />
      </div>
    </div>
  );
}

function ContactsAppDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const { user } = useAuth();
  const staff = user?.role === "owner" || user?.role === "admin" || user?.role === "manager";
  const [stages, setStages] = useState<Stage[]>([]);
  const [folders, setFolders] = useState<Folders | null>(null);
  const [owners, setOwners] = useState<ContactOwner[]>([]);
  // папка запоминается в браузере для каждого сотрудника: все, кроме владельца, начинают с «Мои клиенты»
  const [folder, setFolder] = useState(() => {
    const saved = user ? localStorage.getItem(`hub.contacts.folder:${user.id}`) : null;
    return saved || (user?.role === "owner" ? "all" : "mine");
  });
  const [query, setQuery] = useState("");
  const [q, setQ] = useState("");
  // пришли из «Импорт → Открыть контакты импорта»
  const [fromImport, setFromImport] = useState(takeImportFilter);
  const [list, setList] = useState<Contact[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [panel, setPanel] = useState<null | { mode: "new" | "edit"; contact?: Contact }>(null);

  // переход из сделки или компании: открыть карточку контакта сразу
  useEffect(() => {
    const id = takeContact();
    if (id) contactsApi.get(id).then((c) => setPanel({ mode: "edit", contact: c }), () => undefined);
  }, []);
  const [openGroups, setOpenGroups] = useState({ lifecycle: true, segments: true });
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const req = useRef(0);

  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = (text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4600);
  };

  // поиск уходит на сервер через 300 мс после последней буквы
  useEffect(() => {
    const t = setTimeout(() => setQ(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const meName = user?.name || user?.email || "";
  const meRole = user ? ROLE_LABELS[user.role] : "";

  const loadMeta = useCallback(async () => {
    try {
      const [st, fo] = await Promise.all([stagesApi.list(), contactsApi.folders()]);
      setStages(st);
      setFolders(fo);
    } catch (err) {
      flash(apiErrorText(err));
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    void loadMeta();
    // владельцы карточек — активные и приглашённые пользователи; агенту список не нужен
    api<{ id: string; name: string; email: string; role: Role; status: string }[]>("/users")
      .then((rows) =>
        setOwners(rows.filter((r) => r.status !== "disabled").map((r) => ({ id: r.id, name: r.name || r.email, role: ROLE_LABELS[r.role] ?? r.role }))),
      )
      .catch(() => setOwners([{ id: user.id, name: user.name || user.email, role: ROLE_LABELS[user.role] }]));
  }, [user, loadMeta]);

  const filter = useMemo<ContactQuery>(() => {
    const f: ContactQuery = { q: q || undefined, sort: "activity" };
    if (folder === "mine") f.owner = "me";
    else if (folder === "free") f.owner = "none";
    else if (isSegment(folder)) f.segment = folder;
    else if (folder !== "all") f.stage = folder;
    if (fromImport) f.import_id = fromImport.id;
    return f;
  }, [folder, q, fromImport]);

  const load = useCallback(
    async (offset = 0) => {
      const id = ++req.current;
      if (offset) setMore(true);
      else setLoading(true);
      try {
        const res = await contactsApi.list({ ...filter, limit: PAGE, offset });
        if (id !== req.current) return; // пришёл ответ на старый запрос
        setList((prev) => (offset ? [...prev, ...res.items] : res.items));
        setTotal(res.total);
      } catch (err) {
        if (id === req.current) flash(apiErrorText(err));
      } finally {
        if (id === req.current) {
          setLoading(false);
          setMore(false);
        }
      }
    },
    [filter],
  );

  useEffect(() => {
    if (user) void load(0);
  }, [user, load]);

  const stageOf = (id: string | null) => stages.find((s) => s.id === id);
  const mainStages = stages.filter((s) => !s.lost);
  const lostStages = stages.filter((s) => s.lost);
  const counts: Record<string, number> = { all: folders?.all ?? 0, mine: folders?.mine ?? 0, free: folders?.free ?? 0, ...(folders?.stages ?? {}), ...(folders?.segments ?? {}) };

  const jump = (anchor: string, label: string) => {
    if (goAnchor(anchor)) return;
    flash(t("Раздел «{label}» пока не открывается", { label }));
  };

  const saved = (c: Contact, isNew: boolean) => {
    setPanel(null);
    const st = stageOf(c.stageId);
    flash(
      isNew
        ? st
          ? t("Контакт «{name}» добавлен в базу — этап «{stage}»", { name: c.name, stage: st.name })
          : t("Контакт «{name}» добавлен в базу", { name: c.name })
        : t("Контакт «{name}» обновлён", { name: c.name }),
    );
    void load(0);
    void loadMeta();
  };

  const deleted = (c: Contact) => {
    setPanel(null);
    flash(t("Контакт «{name}» удалён", { name: c.name }));
    void load(0);
    void loadMeta();
  };

  const openFolder = (id: string) => {
    if (id === "ai-agent") {
      jump("#ai-assist", "AI Assist");
      flash(t("AI-агент настраивается в разделе AI Assist — открыли его для вас"));
      return;
    }
    setFolder(id);
    if (user) localStorage.setItem(`hub.contacts.folder:${user.id}`, id);
  };

  const folderLabel =
    folder === "all" ? t("Все контакты") : folder === "mine" ? t("Мои клиенты") : folder === "free" ? t("Без ответственного") : stageOf(folder)?.name ?? contactSegments.find((s) => s.id === folder)?.label ?? "";
  const segmentHint = contactSegments.find((s) => s.id === folder)?.hint;

  return (
    <Band id="contacts-app" tone="muted">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска «окна приложения» */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">{str(brand) || "Hub"} {t("/ рабочее пространство → контакты")}</span>
          <span className="ms-auto inline-flex items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground">
            <span className={cn("size-1.5 rounded-full", counts.all > 0 ? "bg-emerald-500" : "bg-foreground/30")} aria-hidden />
            {t("Контактов: {n}", { n: counts.all })}
          </span>
        </div>

        <div className="flex">
          {/* иконная панель */}
          <div className="hidden w-12 flex-none flex-col items-center gap-1 border-e border-border py-3 lg:flex">
            {RAIL.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => jump(r.anchor, r.label)}
                title={r.label}
                aria-label={r.label}
                className={cn(
                  "grid size-8 cursor-pointer place-items-center rounded-btn transition",
                  r.active ? "bg-primary/12 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <i className={cn(r.icon, "text-[15px] leading-none")} aria-hidden />
              </button>
            ))}
            <span
              className="mt-auto grid size-8 place-items-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground"
              title={meName ? `${meName} · ${meRole}` : undefined}
              aria-hidden
            >
              {initials(meName)}
            </span>
          </div>

          {/* папки */}
          <div className="hidden w-[15.5rem] flex-none flex-col border-e border-border lg:flex">
            <div className="flex items-center gap-1 px-3 pb-1 pt-3">
              <p className="text-[13px] font-semibold">{t("Контакты")}</p>
              <div className="ms-auto flex items-center gap-0.5">
                <button
                  type="button"
                  onClick={() => {
                    setFolder("all");
                    const input = document.getElementById("contacts-search");
                    input?.focus();
                  }}
                  aria-label={t("Поиск по контактам")}
                  title={t("Поиск по контактам")}
                  className="grid size-6 cursor-pointer place-items-center rounded-[5px] text-muted-foreground transition hover:bg-muted hover:text-primary"
                >
                  <Search className="size-3.5" aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => setPanel({ mode: "new" })}
                  aria-label={t("Добавить контакт")}
                  title={t("Добавить контакт")}
                  className="grid size-6 cursor-pointer place-items-center rounded-[5px] text-muted-foreground transition hover:bg-muted hover:text-primary"
                >
                  <i className="ri-add-line text-[14px] leading-none" aria-hidden />
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-0.5 px-2 pt-1">
              <FolderItem active={folder === "all"} icon="ri-contacts-line" label={t("Все контакты")} count={counts.all} onClick={() => openFolder("all")} />
              <FolderItem active={folder === "mine"} icon="ri-user-star-line" label={t("Мои клиенты")} count={counts.mine} onClick={() => openFolder("mine")} />
              <FolderItem active={folder === "free"} icon="ri-user-unfollow-line" label={t("Без ответственного")} count={counts.free} tone="text-amber-600" onClick={() => openFolder("free")} />
              <FolderItem active={false} icon="ri-sparkling-line" label={t("Создать AI-агента")} onClick={() => openFolder("ai-agent")} />
            </div>

            <GroupHead
              label={t("Жизненный цикл")}
              open={openGroups.lifecycle}
              onToggle={() => setOpenGroups((g) => ({ ...g, lifecycle: !g.lifecycle }))}
              onAdd={() => jump("#lifecycle", t("Жизненный цикл"))}
            />
            {openGroups.lifecycle ? (
              <div className="flex flex-col gap-0.5 px-2">
                {mainStages.map((s) => (
                  <FolderItem
                    key={s.id}
                    active={folder === s.id}
                    icon={s.icon}
                    label={s.name}
                    count={counts[s.id]}
                    tone={stageTone(s)}
                    onClick={() => openFolder(s.id)}
                  />
                ))}
                {lostStages.length ? (
                  <p className="px-2 pb-0.5 pt-1.5 text-[9.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/80">{t("Потерянные этапы")}</p>
                ) : null}
                {lostStages.map((s) => (
                  <FolderItem
                    key={s.id}
                    active={folder === s.id}
                    icon={s.icon}
                    label={s.name}
                    count={counts[s.id]}
                    tone={stageTone(s)}
                    onClick={() => openFolder(s.id)}
                  />
                ))}
              </div>
            ) : null}

            <GroupHead
              label={t("Сегменты")}
              open={openGroups.segments}
              onToggle={() => setOpenGroups((g) => ({ ...g, segments: !g.segments }))}
            />
            {openGroups.segments ? (
              <div className="flex flex-col gap-0.5 px-2">
                {contactSegments.map((s) => (
                  <FolderItem
                    key={s.id}
                    active={folder === s.id}
                    icon={s.icon}
                    label={s.label}
                    count={counts[s.id]}
                    onClick={() => openFolder(s.id)}
                  />
                ))}
              </div>
            ) : null}

            {/* текущий пользователь */}
            <div className="mt-auto border-t border-border p-2.5">
              <p className="truncate text-[10.5px] font-semibold leading-tight text-foreground/80">{meName}</p>
              <p className="mt-0.5 text-[10px] leading-relaxed text-muted-foreground">
                {meRole}
                {staff ? "" : ` · ${t("видны только ваши контакты")}`}
              </p>
            </div>
          </div>

          {/* основная область */}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
              <div className="relative min-w-0 flex-1 sm:max-w-xs">
                <Search className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <input
                  id="contacts-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("Поиск контактов")}
                  aria-label={t("Поиск контактов")}
                  className="h-8 w-full box-border rounded-btn border border-border bg-background ps-8 pe-7 text-[12px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                />
                {query ? (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    aria-label={t("Очистить поиск")}
                    className="absolute end-1.5 top-1/2 grid size-5 -translate-y-1/2 cursor-pointer place-items-center rounded-full text-muted-foreground transition hover:bg-muted hover:text-foreground"
                  >
                    <X className="size-3" aria-hidden />
                  </button>
                ) : null}
              </div>

              <div className="ms-auto flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => jump("#contacts-import", t("Импорт контактов"))}
                  className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-2.5 text-[11.5px] font-semibold text-muted-foreground transition hover:border-primary/50 hover:text-primary"
                >
                  <i className="ri-upload-cloud-2-line text-[13px] leading-none" aria-hidden />
                  {t("Импорт")}
                </button>
                <button
                  type="button"
                  onClick={() => setPanel({ mode: "new" })}
                  className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3 text-[11.5px] font-semibold text-primary-foreground transition hover:brightness-110"
                >
                  <i className="ri-add-line text-[13px] leading-none" aria-hidden />
                  {t("Добавить контакт")}
                </button>
              </div>
            </div>

            {/* мобильные папки */}
            <div className="flex gap-1.5 overflow-x-auto border-b border-border px-2.5 py-2 lg:hidden">
              {[{ id: "all", label: t("Все") }, ...stages.map((st) => ({ id: st.id, label: st.name })), ...contactSegments].map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => openFolder(f.id)}
                  className={cn(
                    "inline-flex flex-none cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
                    folder === f.id ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground",
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* строка текущей папки */}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border px-3 py-1.5">
              <h4 className="text-[12.5px] font-semibold">{folderLabel}</h4>
              <span className="text-[10.5px] tabular-nums text-muted-foreground">{loading ? "…" : total}</span>
              {segmentHint ? <span className="text-[10.5px] text-muted-foreground">· {segmentHint}</span> : null}
              {q ? <span className="text-[10.5px] text-muted-foreground">{t("· поиск: «{q}»", { q })}</span> : null}
              {fromImport ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10.5px] font-semibold text-primary">
                  <i className="ri-upload-cloud-2-line leading-none" aria-hidden />
                  {t("из импорта «{file}»", { file: fromImport.fileName })}
                  <button
                    type="button"
                    onClick={() => setFromImport(null)}
                    aria-label={t("Показать все контакты")}
                    className="ms-0.5 cursor-pointer rounded-full leading-none hover:text-foreground"
                  >
                    <i className="ri-close-line" aria-hidden />
                  </button>
                </span>
              ) : null}
            </div>

            {/* содержимое */}
            <div className="min-h-[24rem]">
              {loading && list.length === 0 ? (
                <p className="px-5 py-10 text-center text-[12px] text-muted-foreground">{t("Загружаем контакты…")}</p>
              ) : list.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 px-5 py-10 text-center">
                  <EmptyArt />
                  <h4 className="mt-1 text-[15px] font-semibold leading-snug">
                    {q || folder !== "all" ? t("Здесь пока пусто") : contactEmpty.title}
                  </h4>
                  <p className="max-w-sm text-[12px] leading-relaxed text-muted-foreground">
                    {q
                      ? t("По запросу «{q}» никого не нашли — попробуйте другое имя, телефон или почту.", { q })
                      : folder !== "all"
                        ? t("В папке «{folder}» нет контактов. Выберите другую папку.", { folder: folderLabel })
                        : contactEmpty.text}
                  </p>
                  <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                    <button
                      type="button"
                      onClick={() => jump("#contacts-import", t("Импорт контактов"))}
                      className="inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-3.5 text-[12px] font-semibold transition hover:border-primary/50 hover:text-primary"
                    >
                      <i className="ri-upload-cloud-2-line text-[14px] leading-none" aria-hidden />
                      {t("Импорт контактов")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPanel({ mode: "new" })}
                      className="inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110"
                    >
                      <i className="ri-user-add-line text-[14px] leading-none" aria-hidden />
                      {t("Добавить контакт")}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                <ul className="flex flex-col divide-y divide-border/60">
                  {list.map((c) => {
                    const st = stageOf(c.stageId);
                    const owner = owners.find((o) => o.id === c.ownerId);
                    return (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => setPanel({ mode: "edit", contact: c })}
                          className="grid w-full cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2.5 px-3 py-2 text-start transition hover:bg-muted/60 md:grid-cols-[auto_minmax(0,1.1fr)_minmax(0,.9fr)_auto_auto]"
                        >
                          <span className="relative flex-none">
                            <span className="grid size-8 place-items-center rounded-full bg-primary/15 text-[10.5px] font-bold text-primary" aria-hidden>
                              {initials(c.firstName || c.lastName ? `${c.firstName} ${c.lastName}` : c.name)}
                            </span>
                            {c.blocked ? (
                              <span className="absolute -end-0.5 -top-0.5 grid size-4 place-items-center rounded-full bg-foreground/70 text-[9px] text-background" title={t("Заблокирован")} aria-hidden>
                                <i className="ri-forbid-2-line leading-none" />
                              </span>
                            ) : null}
                          </span>

                          <span className="min-w-0">
                            <span className="block truncate text-[12.5px] font-semibold">{c.name}</span>
                            <span className="mt-0.5 flex items-center gap-1.5 truncate text-[10.5px] text-muted-foreground">
                              {c.phone || c.email || t("нет контактов")}
                              {c.company ? ` · ${c.company}` : ""}
                            </span>
                          </span>

                          <span className="hidden min-w-0 items-center gap-1.5 md:flex">
                            {st ? (
                              <span className={cn("inline-flex flex-none items-center gap-1 rounded-full px-1.5 py-0.5 text-[9.5px] font-semibold", stageTone(st))}>
                                <i className={cn(st.icon, "text-[10px] leading-none")} aria-hidden />
                                {st.name}
                              </span>
                            ) : null}
                            {c.tags.slice(0, 2).map((t) => (
                              <span key={t.id} className="inline-flex flex-none items-center gap-0.5 rounded-full bg-muted px-1.5 py-0.5 text-[9.5px] font-medium text-foreground/75">
                                {t.emoji ? <span aria-hidden>{t.emoji}</span> : null}
                                {t.name}
                              </span>
                            ))}
                            {c.tags.length > 2 ? <span className="flex-none text-[9.5px] text-muted-foreground">+{c.tags.length - 2}</span> : null}
                            {owner ? <span className="truncate text-[10px] text-muted-foreground">{owner.name}</span> : null}
                          </span>

                          <span className="hidden whitespace-nowrap text-[10px] text-muted-foreground sm:inline">{relTime(c.lastActivityAt ?? c.createdAt)}</span>

                          <span className="grid size-7 flex-none place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-primary" aria-hidden>
                            <i className="ri-arrow-right-s-line text-[14px] leading-none" />
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {list.length < total ? (
                  <div className="flex justify-center border-t border-border/60 p-2.5">
                    <button
                      type="button"
                      onClick={() => void load(list.length)}
                      disabled={more}
                      className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-3 text-[11.5px] font-semibold text-muted-foreground transition hover:border-primary/50 hover:text-primary disabled:cursor-wait disabled:opacity-60"
                    >
                      {more ? t("Загружаем…") : t("Показать ещё ({n})", { n: total - list.length })}
                    </button>
                  </div>
                ) : null}
                </>
              )}
            </div>
          </div>
        </div>

        {/* подсказки */}
        <div className="grid gap-2 border-t border-border p-3.5 sm:grid-cols-3">
          {contactsAppHints.map((h) => (
            <div key={h.title} className="rounded-btn border border-border bg-background/40 p-2.5">
              <p className="flex items-center gap-1.5 text-[11.5px] font-semibold">
                <i className={cn(h.icon, "text-[13px] leading-none text-primary")} aria-hidden />
                {h.title}
              </p>
              <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">{h.text}</p>
            </div>
          ))}
        </div>

        {toast ? (
          <div
            role="status"
            className="pointer-events-none absolute inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
          >
            <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
            {toast}
          </div>
        ) : null}

        {panel && user ? (
          <ContactPanel
            key={panel.contact?.id ?? "new"}
            mode={panel.mode}
            contact={panel.contact}
            stages={stages}
            owners={owners}
            meId={user.id}
            staff={staff}
            onClose={() => setPanel(null)}
            onSaved={saved}
            onDeleted={deleted}
          />
        ) : null}
      </div>
    </Band>
  );
}

function FolderItem({
  active,
  icon,
  label,
  count,
  tone,
  onClick,
}: {
  active: boolean;
  icon: string;
  label: string;
  count?: number;
  tone?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex w-full cursor-pointer items-center gap-2 rounded-btn px-2 py-[7px] text-start text-[12px] font-medium transition",
        active ? "bg-primary/15 text-primary" : "text-foreground/75 hover:bg-muted hover:text-foreground",
      )}
    >
      {tone ? (
        <span className={cn("grid size-4 flex-none place-items-center rounded-[4px] text-[10px] leading-none", tone)} aria-hidden>
          <i className={icon} />
        </span>
      ) : (
        <span className={cn("grid size-5 flex-none place-items-center text-[13px] leading-none", active ? "text-primary" : "text-muted-foreground")} aria-hidden>
          <i className={icon} />
        </span>
      )}
      <span className={cn("min-w-0 flex-1 truncate", tone && "text-[11.5px]")}>{label}</span>
      {typeof count === "number" && count > 0 ? (
        <span className={cn("flex-none tabular-nums text-[10.5px]", active ? "text-primary" : "text-muted-foreground/80")}>{count}</span>
      ) : null}
    </button>
  );
}

function GroupHead({ label, open, onToggle, onAdd }: { label: string; open: boolean; onToggle: () => void; onAdd?: () => void }) {
  return (
    <div className="mt-2 flex items-center gap-1 px-1">
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 text-start">
        <i className={cn("ri-arrow-down-s-line text-[13px] leading-none text-muted-foreground transition", !open && "-rotate-90")} aria-hidden />
        <span className="truncate text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{label}</span>
      </button>
      {onAdd ? (
        <button
          type="button"
          onClick={onAdd}
          aria-label={t("Открыть «{label}»", { label })}
          title={t("Открыть «{label}»", { label })}
          className="grid size-5 flex-none cursor-pointer place-items-center rounded-[5px] text-muted-foreground transition hover:bg-muted hover:text-primary"
        >
          <i className="ri-add-line text-[12px] leading-none" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}

export default ContactsAppDemo;